import { gql, useMutation, useQuery } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DialogActions, DialogTitle, Grid, TextField, Alert } from "@mui/material";
import TimezoneSelect, { browserTimezone } from "../Components/TimezoneSelect";
import { Dashboard } from "@mui/icons-material";
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  FormControlLabel,
  CircularProgress,
  Dialog,
  DialogContent,
  Link as MuiLink,
  Paper,
} from "@mui/material";
import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { API_URL, IMAGES_API_URL } from "../constants";
import { ROLES } from "../Roles";
import { Icon } from "@iconify/react";

export default function EventPage() {
  const { eventId } = useParams();
  const { data, loading, error, refetch } = useQuery(GET_EVENT, {
    variables: { eventId },
  });

  if (loading) {
    return <LinearProgress />;
  }

  if (error) {
    return <Typography color="error">{error?.message}</Typography>;
  }
  if (!data?.event) {
    return <Typography>No Event Found</Typography>;
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4" gutterBottom>
        {data.event.name}
      </Typography>
      <EventInsightsPanel event={data.event} insights={data.eventInsights} />
      <Stack
        direction="row"
        justifyContent="space-evenly"
        sx={{ background: "lightgrey", py: 3 }}
      >
        <Stack direction="column">
          <Typography variant="h5">Event Participants/Stands</Typography>
          <MuiLink component={Link} to="stands" underline="hover">
            Manage List
          </MuiLink>
        </Stack>
        <Stack direction="column">
          <Typography variant="h5">Event Floor Plan</Typography>
          <MuiLink component={Link} to="floorplan" underline="hover">
            Manage Floor Plan
          </MuiLink>
        </Stack>
        <Stack direction="column">
          <Typography variant="h5">Outbound Integrations</Typography>
          <MuiLink component={Link} to="integrations" underline="hover">
            Manage Webhooks
          </MuiLink>
        </Stack>
        <Box height={30} />
      </Stack>
      <Typography variant="h5">Event Roles</Typography>

      {/* Active Roles Summary */}
      <Box
        sx={{
          backgroundColor: "#f0f8ff",
          border: "1px solid #b3d9ff",
          borderRadius: 1,
          p: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" color="primary" gutterBottom>
          Active Roles Summary
        </Typography>
        <Typography variant="body1">
          <strong>
            {data.event.roles?.split(",").filter((role) => role.trim() !== "")
              .length || 0}
          </strong>{" "}
          active roles out of <strong>{ROLES.length}</strong> total roles
        </Typography>
        {data.event.roles && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
            Active roles:{" "}
            {data.event.roles
              .split(",")
              .filter((role) => role.trim() !== "")
              .sort()
              .join(", ")}
          </Typography>
        )}
      </Box>

      <ActiveRolesTable event={data.event} refetch={refetch} />
      <OfferingsTable eventId={data.event.id} />

      <Box height={30} />
      <Typography variant="h5">Event Images</Typography>

      <TableContainer>
        <Table>
          <TableHead>
            <TableCell>Name</TableCell>
            <TableCell>Image</TableCell>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Banner</TableCell>
              <TableCell>
                <EventBanner event={data.event} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Logo</TableCell>
              <TableCell>
                <EventLogo event={data.event} />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Favicon</TableCell>
              <TableCell>
                <EventFavicon event={data.event} />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
}

function EventBanner({ event }) {
  const [upload, { loading, error }] = useMutation(gql`
    mutation ($file: Upload!, $eventId: String!) {
      uploadEventBanner(eventId: $eventId, file: $file)
    }
  `);

  const [deleteBanner] = useMutation(gql`
    mutation ($eventId: String!) {
      deleteEventBanner(eventId: $eventId)
    }
  `);

  function onChange({
    target: {
      validity,
      files: [file],
    },
  }) {
    if (validity.valid) upload({ variables: { eventId: event.id, file } });
  }

  return (
    <Stack spacing={1}>
      <Box>
        <img
          src={IMAGES_API_URL + `banner/${event.id}.jpeg?t=${Date.now()}`}
          height={200}
        />
      </Box>

      <input type="file" accept="image/jpeg" required onChange={onChange} />
      <Box>
        <Button
          onClick={() => deleteBanner({ variables: { eventId: event.id } })}
          color="error"
        >
          Remove
        </Button>
      </Box>

      {loading && <LinearProgress />}
      {error && (
        <Typography color="error">{JSON.stringify(error, null, 0)}</Typography>
      )}
    </Stack>
  );
}

function EventLogo({ event }) {
  const [upload, { loading, error }] = useMutation(gql`
    mutation ($file: Upload!, $eventId: String!) {
      uploadEventLogo(eventId: $eventId, file: $file)
    }
  `);

  const [deleteLogo] = useMutation(gql`
    mutation ($eventId: String!) {
      deleteEventLogo(eventId: $eventId)
    }
  `);

  function onChange({
    target: {
      validity,
      files: [file],
    },
  }) {
    if (validity.valid) upload({ variables: { eventId: event.id, file } });
  }

  return (
    <Stack spacing={1}>
      <Box>
        <img
          src={IMAGES_API_URL + `logo/${event.id}.png?t=${Date.now()}`}
          height={80}
        />
      </Box>

      <input type="file" accept="image/png" required onChange={onChange} />
      <Box>
        <Button
          onClick={() => deleteLogo({ variables: { eventId: event.id } })}
          color="error"
        >
          Remove
        </Button>
      </Box>

      {loading && <LinearProgress />}
      {error && (
        <Typography color="error">{JSON.stringify(error, null, 0)}</Typography>
      )}
    </Stack>
  );
}

function EventInsightsPanel({ event, insights }) {
  const [editOpen, setEditOpen] = React.useState(false);
  if (!insights) {
    return null;
  }

  const rows = [
    {
      label: "Event Start Date",
      value: insights.startAt || event.startAt || "Not set",
    },
    {
      label: "Event End Date",
      value: insights.endAt || event.endAt || "Not set",
    },
    {
      label: "Event Start Time",
      value: event.startTime || "Not set",
    },
    {
      label: "Event End Time",
      value: event.endTime || "Not set",
    },
    {
      label: "Event Timezone",
      value: event.timezone || "Not set (Reception falls back to server timezone)",
    },
    {
      label: "Location",
      value: event.location || "Not set",
    },
    {
      label: "Total Registrations (excl. crew)",
      value: insights.totalRegistrations ?? 0,
    },
    {
      label: "Crew - Reception",
      value: insights.crewReceptionCount ?? 0,
    },
    { label: "Crew - Printer", value: insights.crewPrinterCount ?? 0 },
    {
      label: "Crew - Entry Gate Scanner",
      value: insights.crewEntryGateScannerCount ?? 0,
    },
    {
      label: "Crew - Exit Gate Scanner",
      value: insights.crewExitGateScannerCount ?? 0,
    },
    {
      label: "Leadgen Admin Accounts",
      value: insights.leadgenAdminCount ?? 0,
    },
    { label: "Sponsors", value: insights.sponsorCount ?? 0 },
    {
      label: "Crew Admin Password",
      value: insights.crewAdminPassword || "Not available",
    },
    {
      label: "Crew Superadmin Password",
      value: insights.crewSuperAdminPassword || "Not available",
    },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>
          Event Insights
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Icon icon="mdi:calendar-edit" />}
          onClick={() => setEditOpen(true)}
        >
          Edit datetime + timezone
        </Button>
      </Stack>
      {editOpen && (
        <EventDatetimeDialog
          event={event}
          onClose={() => setEditOpen(false)}
        />
      )}
      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.label}>
                  <TableCell sx={{ fontWeight: 600 }}>{row.label}</TableCell>
                  <TableCell>{row.value ?? "N/A"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {(insights.crewCategoryColumn || insights.crewPasswordColumn) && (
          <Box
            px={2}
            py={1}
            sx={{ fontStyle: "italic", color: "text.secondary" }}
          >
            Crew counts sourced from{" "}
            {insights.crewCategoryColumn || "available"} column
            {insights.crewPasswordColumn
              ? `; passwords from ${insights.crewPasswordColumn}`
              : ""}
            .
          </Box>
        )}
      </Paper>
    </Box>
  );
}

function EventFavicon({ event }) {
  const [upload, { loading, error }] = useMutation(gql`
    mutation ($file: Upload!, $eventId: String!) {
      uploadEventFavicon(eventId: $eventId, file: $file)
    }
  `);

  const [deleteFavicon] = useMutation(gql`
    mutation ($eventId: String!) {
      deleteEventFavicon(eventId: $eventId)
    }
  `);

  function onChange({
    target: {
      validity,
      files: [file],
    },
  }) {
    if (validity.valid) upload({ variables: { eventId: event.id, file } });
  }

  return (
    <Stack spacing={1}>
      <Box>
        <img
          src={
            IMAGES_API_URL +
            `favicon/${event.id}/favicon-32x32.png?t=${Date.now()}`
          }
          height={32}
        />
      </Box>

      <input type="file" accept=".zip" required onChange={onChange} />
      <Box>
        <Button
          onClick={() => deleteFavicon({ variables: { eventId: event.id } })}
          color="error"
        >
          Remove
        </Button>
      </Box>

      {loading && <LinearProgress />}
      {error && (
        <Typography color="error">{JSON.stringify(error, null, 0)}</Typography>
      )}
    </Stack>
  );
}


/**
 * SUB-REGISTRATION OFFERINGS (workshops, and later dinners/activities).
 *
 * Deliberately SEPARATE from Active Roles. RHIC has 47 workshops; listing them as roles would
 * mean 47 ROLE enum entries across six repositories, 47 permission tokens, and 47 phantom rows in
 * every role-iterating screen. An offering is identified by `event_addons.code` and is NOT a role.
 *
 * ONLY Badge and Printer Badge are shown. FORM, EMAIL, DATA and RESPONSE are deliberately ABSENT
 * rather than disabled: `form.graphql` (11) and `email.graphql` (3) still require the ROLE enum,
 * and DATA/RESPONSE are served by api-reception-v2, which this app does not talk to. Rendering
 * buttons that error is worse than not rendering them.
 *
 * Listing writes NOTHING. Opening a configuration page creates nothing until the organizer saves.
 */
function OfferingsTable({ eventId }) {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery(
    gql`
      query ($eventId: String!, $type: String) {
        eventOfferings(eventId: $eventId, type: $type) {
          code
          name
          type
          status
        }
      }
    `,
    { variables: { eventId, type: "workshop" }, fetchPolicy: "cache-and-network" },
  );

  if (loading && !data) return <LinearProgress />;
  // An event with no add-ons is the normal case, not an error worth shouting about.
  if (error || !data?.eventOfferings?.length) return null;

  const offerings = data.eventOfferings;

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Workshops
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {offerings.length} sub-registration offering{offerings.length === 1 ? "" : "s"}. These are
        not roles — each is configured by its own code.
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Workshop</TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {offerings.map((o, i) => (
              <TableRow key={o.code} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell sx={{ maxWidth: 420 }}>{o.name || o.code}</TableCell>
                <TableCell>
                  <code style={{ fontSize: 12 }}>{o.code}</code>
                </TableCell>
                <TableCell>{o.status}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      /* Routes to :role — the offering CODE travels as the legacy key. The
                         api-admin validator (utils/badgeKeyValidation.js) is authoritative. */
                      onClick={() => navigate(`${o.code}/badge`)}
                      startIcon={<Icon icon="emojione-monotone:name-badge" />}
                      variant="contained"
                      color="error"
                      size="small"
                    >
                      Badge
                    </Button>
                    <Button
                      onClick={() => navigate(`${o.code}/printer-badge`)}
                      startIcon={<Icon icon="material-symbols:print" />}
                      variant="contained"
                      color="secondary"
                      size="small"
                    >
                      Printer Badge
                    </Button>
                    <Button
                      /* Email config is keyed by `{eventId}_emails.role` = the offering code,
                         exactly as the PHP reference already reads it. Opening the page creates
                         nothing; a row is written only when the organizer saves. */
                      onClick={() => navigate(`${o.code}/email`)}
                      startIcon={<Icon icon="material-symbols:mark-email-unread" />}
                      variant="contained"
                      color="primary"
                      size="small"
                    >
                      Email
                    </Button>
                    {/* DATA / FORM / RESPONSE — the same existing role pages, reached with the
                        offering code as the legacy key. These reuse the EXISTING routes and
                        resolvers (api-admin `applicants`/`form`/`response`, all now String! +
                        assertValidBadgeKey-guarded); nothing workshop-specific is duplicated here,
                        and opening any of them writes no row. */}
                    <Button
                      onClick={() => navigate(`${o.code}/data`)}
                      startIcon={<Icon icon="material-symbols:table-rows" />}
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      Data
                    </Button>
                    <Button
                      onClick={() => navigate(`${o.code}/form`)}
                      startIcon={<Icon icon="material-symbols:list-alt" />}
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      Form
                    </Button>
                    <Button
                      onClick={() => navigate(`${o.code}/response`)}
                      startIcon={<Icon icon="material-symbols:reply" />}
                      variant="outlined"
                      color="primary"
                      size="small"
                    >
                      Response
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function ActiveRolesTable({ event, refetch }) {
  const activeRoles = event.roles?.split(",") || [];
  const navigate = useNavigate();

  // Sort roles: active roles first (alphabetically), then inactive roles (alphabetically)
  const sortedRoles = ROLES.sort((a, b) => {
    const aActive = activeRoles.includes(a);
    const bActive = activeRoles.includes(b);

    // If both are active or both are inactive, sort alphabetically
    if (aActive === bActive) {
      return a.localeCompare(b);
    }

    // Active roles come first
    return aActive ? -1 : 1;
  });

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableCell>#</TableCell>
          <TableCell>Role</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Actions</TableCell>
        </TableHead>
        <TableBody>
          {sortedRoles.map((r, i) => {
            const live = activeRoles.includes(r);
            return (
              <TableRow
                key={r}
                sx={{ backgroundColor: live ? "#e8f5e8" : "inherit" }}
              >
                <TableCell>{i + 1}</TableCell>
                <TableCell
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: live ? "bold" : "normal",
                  }}
                >
                  {r}
                </TableCell>
                <TableCell>
                  <ActivateRoleButton
                    live={live}
                    eventId={event.id}
                    role={r}
                    refetch={refetch}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1}>
                    <Button
                      onClick={() => navigate(`${r}/data`)}
                      startIcon={<Icon icon="bxs:data" />}
                      variant="contained"
                    >
                      Data
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/form`)}
                      startIcon={<Icon icon="ic:baseline-dynamic-form" />}
                      variant="contained"
                      disabled={!live}
                      color="info"
                    >
                      Form
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/link`)}
                      startIcon={<Icon icon="entypo:link" />}
                      variant="contained"
                      disabled={!live}
                      color="secondary"
                    >
                      Link
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/badge`)}
                      startIcon={<Icon icon="emojione-monotone:name-badge" />}
                      variant="contained"
                      disabled={!live}
                      color="error"
                    >
                      Badge
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/printer-badge`)}
                      startIcon={<Icon icon="material-symbols:print" />}
                      variant="contained"
                      disabled={!live}
                      color="secondary"
                    >
                      Printer Badge
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/email`)}
                      startIcon={
                        <Icon icon="material-symbols:mark-email-unread" />
                      }
                      variant="contained"
                      disabled={!live}
                      color="success"
                    >
                      Email
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/response`)}
                      startIcon={<Icon icon="clarity:success-standard-solid" />}
                      variant="contained"
                      disabled={!live}
                      color="warning"
                    >
                      Response
                    </Button>
                    <Button
                      onClick={() => navigate(`${r}/certificate`)}
                      startIcon={<Icon icon="clarity:certificate-solid" />}
                      variant="contained"
                      disabled={!live}
                      color="error"
                    >
                      Certificate
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function ActivateRoleButton({ live, eventId, role, refetch }) {
  const [toggleEventRole, { loading, error }] = useMutation(gql`
    mutation ($eventId: String!, $role: ROLE!, $live: Boolean!) {
      toggleEventRole(eventId: $eventId, role: $role, live: $live)
    }
  `);
  const handleChange = (e) => {
    // console.log(e.currentTarget.checked);
    toggleEventRole({
      variables: { eventId, role, live: e.currentTarget.checked },
    }).then(() => refetch());
  };

  if (loading) {
    return <CircularProgress />;
  }
  if (error) {
    return (
      <Dialog open>
        <DialogContent>{error.message}</DialogContent>
      </Dialog>
    );
  }

  return (
    <Box>
      <FormControlLabel
        control={<Switch checked={live} onChange={handleChange} />}
        label="Live"
      />
    </Box>
  );
}

const GET_EVENT = gql`
  query GetEvent($eventId: String!) {
    event(eventId: $eventId) {
      id
      name
      roles
      created
      startAt
      endAt
      startTime
      endTime
      location
      timezone
    }
    eventInsights(eventId: $eventId) {
      eventId
      name
      startAt
      endAt
      totalRegistrations
      crewReceptionCount
      crewPrinterCount
      crewEntryGateScannerCount
      crewExitGateScannerCount
      leadgenAdminCount
      sponsorCount
      crewAdminPassword
      crewSuperAdminPassword
      crewCategoryColumn
      crewPasswordColumn
    }
  }
`;

const UPDATE_EVENT = gql`
  mutation (
    $eventId: String!
    $name: String
    $startAt: String
    $endAt: String
    $startTime: String
    $endTime: String
    $location: String
    $timezone: String
  ) {
    updateEvent(
      eventId: $eventId
      name: $name
      startAt: $startAt
      endAt: $endAt
      startTime: $startTime
      endTime: $endTime
      location: $location
      timezone: $timezone
    ) {
      id
      name
      startAt
      endAt
      startTime
      endTime
      location
      timezone
    }
  }
`;

function parseDateOrNull(s) {
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

function parseTimeOrNull(s) {
  if (!s) return null;
  // s is "HH:mm" — attach to today's date so TimePicker can render it.
  const today = new Date();
  const [h, m] = String(s).split(":").map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  today.setHours(h, m, 0, 0);
  return today;
}

function EventDatetimeDialog({ event, onClose }) {
  const [updateEvent, { loading, error }] = useMutation(UPDATE_EVENT);
  const { control, handleSubmit } = useForm({
    defaultValues: {
      startAt: parseDateOrNull(event.startAt),
      endAt: parseDateOrNull(event.endAt),
      startTime: parseTimeOrNull(event.startTime),
      endTime: parseTimeOrNull(event.endTime),
      location: event.location || "",
      timezone: event.timezone || browserTimezone(),
    },
    mode: "onChange",
  });

  const onSubmit = (f) => {
    const fmtDate = (d) =>
      d instanceof Date && !isNaN(d) ? d.toISOString().slice(0, 10) : undefined;
    const fmtTime = (d) =>
      d instanceof Date && !isNaN(d)
        ? `${String(d.getHours()).padStart(2, "0")}:${String(
            d.getMinutes()
          ).padStart(2, "0")}`
        : undefined;

    updateEvent({
      variables: {
        eventId: event.id,
        startAt: fmtDate(f.startAt),
        endAt: fmtDate(f.endAt),
        startTime: fmtTime(f.startTime),
        endTime: fmtTime(f.endTime),
        location: f.location || undefined,
        timezone: f.timezone || undefined,
      },
      refetchQueries: ["GetEvent"],
      awaitRefetchQueries: true,
    })
      .then(() => onClose())
      .catch(() => {});
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit event datetime + timezone</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error.message}
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="startAt"
                  render={({ field }) => (
                    <DatePicker
                      label="Start Date"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="endAt"
                  render={({ field }) => (
                    <DatePicker
                      label="End Date"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="startTime"
                  render={({ field }) => (
                    <TimePicker
                      label="Start Time"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  control={control}
                  name="endTime"
                  render={({ field }) => (
                    <TimePicker
                      label="End Time"
                      value={field.value}
                      onChange={field.onChange}
                      slotProps={{ textField: { fullWidth: true } }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="location"
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location"
                      fullWidth
                      variant="standard"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="timezone"
                  render={({ field }) => (
                    <TimezoneSelect
                      value={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={loading}
          >
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}
